import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "../GeneralDisclaimer/general.css";

const StakingUserAgreement = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Staking User Agreement – AGCX Crypto Exchange</title>
        <meta
          name="description"
          content="Read the Staking User Agreement for AGCX. Terms for locked, flexible, and tokenized staking programs on the Arab Global Crypto Exchange platform."
        />
        <meta
          name="keywords"
          content="staking user agreement, AGCX staking, crypto staking terms, locked staking, flexible staking"
        />
      </Helmet>

      <div className="hero-banner-style top-section-gap">
        <section className="faq_sec">
          <div className="container">
            <div className="row align-items-center justify-content-center">
              <div className="col-lg-8 col-md-12">
                <div className="section-title">
                  <h1 className="text-gradient text-center">Staking User Agreement</h1>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="termsofuse">
          <div className="container">
            <p>
              <strong>Effective Date:</strong> 08 April 2026<br />
              <strong>Platform:</strong> Arab Global Crypto Exchange (AGCX)<br />
              <strong>Operator:</strong> Lunexor s.r.o., Kurzova 2222/16, 155 00 Prague 5, Czech Republic
            </p>
            <p>
              This Staking User Agreement (&quot;Agreement&quot;) governs your participation in staking programs offered through the AGCX platform. By checking the acceptance box and subscribing to any staking product, you confirm that you have read, understood, and agreed to this Agreement, together with the AGCX{" "}
              <Link to="/TermsofUse">Terms of Use</Link>,{" "}
              <Link to="/RiskDisclosure">Risk Disclosure</Link>, and{" "}
              <Link to="/general-disclaimer">General Disclaimer</Link>.
            </p>

            <h3>1. Definitions</h3>
            <ul>
              <li><strong>Staking Program</strong> — A reward program launched by AGCX for specific digital assets, under which eligible users may allocate supported assets from their AGCX account to earn rewards.</li>
              <li><strong>Locked Staking</strong> — A program with a fixed subscription and redemption period. Early redemption may be restricted or subject to penalties.</li>
              <li><strong>Flexible Staking</strong> — A program that generally allows subscription and redemption with fewer lock restrictions, subject to product rules and available liquidity.</li>
              <li><strong>Tokenized Staking</strong> — A program where staked assets or rewards may be represented through tokenized instruments, as described in the relevant product page.</li>
              <li><strong>APR / Reward Rate</strong> — The annualized or stated reward percentage displayed for a product. Displayed rates are indicative and may change.</li>
              <li><strong>Digital Assets</strong> — Cryptocurrencies, tokens, and other virtual assets supported by AGCX for staking.</li>
            </ul>

            <h3>2. Eligibility</h3>
            <p>To use AGCX Staking services, you must:</p>
            <ul>
              <li>Be at least 18 years of age (or the legal age in your jurisdiction)</li>
              <li>Complete AGCX account registration and identity verification (KYC), where required</li>
              <li>Reside in or access the platform from a jurisdiction where staking services are permitted</li>
              <li>Not be subject to sanctions, restricted-person lists, or platform suspension</li>
              <li>Have sufficient balance in the supported wallet type (e.g. Spot or Earning wallet) for the selected product</li>
            </ul>
            <p>AGCX may refuse, suspend, or terminate staking access for any user who does not meet eligibility requirements.</p>

            <h3>3. How Staking Works</h3>
            <p>When you subscribe to a Staking Program:</p>
            <ul>
              <li>The subscribed amount is allocated from your selected wallet and locked or reserved according to the product terms</li>
              <li>Rewards are calculated based on the product&apos;s stated rules, duration, tier, and available balance</li>
              <li>Reward distribution schedules (daily, at maturity, or otherwise) are shown on the product page and may vary by program</li>
              <li>Unless otherwise stated, staking subscriptions are generally free of platform subscription fees; network or third-party fees may still apply</li>
              <li>You may continue to use other AGCX services during the staking period, subject to wallet balance and product restrictions</li>
            </ul>

            <h3>4. Product Terms and Displayed Information</h3>
            <p>Each staking product page shows key parameters including, where applicable:</p>
            <ul>
              <li>Supported asset and minimum / maximum subscription amount</li>
              <li>Staking type (Locked, Flexible, or Tokenized)</li>
              <li>Duration, lock period, or redemption window</li>
              <li>Indicative APR or reward range</li>
              <li>Early withdrawal penalty, if any</li>
              <li>Reward distribution method and schedule</li>
            </ul>
            <p>
              You are responsible for reviewing the product-specific details before confirming any subscription. By clicking &quot;Confirm&quot; or equivalent, you accept the terms shown for that specific product at the time of subscription.
            </p>

            <h3>5. Rewards — No Guarantee</h3>
            <p>AGCX does not guarantee any proceeds, fixed returns, or capital protection under any Staking Program. You acknowledge that:</p>
            <ul>
              <li>Displayed APR or reward rates are estimates and may be adjusted before or during the program</li>
              <li>Actual rewards depend on platform rules, subscribed amount, duration, market conditions, and product availability</li>
              <li>Rewards may be paid in the same asset, a different supported asset, or platform-defined reward tokens, as stated on the product page</li>
              <li>Past reward performance does not guarantee future results</li>
            </ul>

            <h3>6. Lock Periods, Redemption, and Early Withdrawal</h3>
            <ul>
              <li><strong>Locked products:</strong> Assets remain locked until the stated maturity date unless early redemption is permitted</li>
              <li><strong>Early withdrawal:</strong> Where allowed, early redemption may reduce or forfeit rewards and may incur a penalty percentage as displayed at subscription</li>
              <li><strong>Flexible products:</strong> Redemption is subject to processing time, liquidity, and product rules shown on the platform</li>
              <li>AGCX may delay redemption processing during maintenance, security reviews, regulatory requirements, or force majeure events</li>
            </ul>

            <h3>7. Risks</h3>
            <p>Staking digital assets involves significant risk. By participating, you accept that:</p>
            <ul>
              <li>Digital asset prices are highly volatile and you may lose part or all of your staked value</li>
              <li>Smart contract, protocol, network, or validator failures may affect rewards or redemption</li>
              <li>Regulatory changes may restrict, suspend, or terminate staking services in your jurisdiction</li>
              <li>Platform downtime, cyber incidents, or third-party service failures may delay rewards or redemption</li>
              <li>Tax obligations arising from staking rewards are your sole responsibility</li>
            </ul>
            <p>Please read the AGCX <Link to="/RiskDisclosure">Risk Disclosure</Link> for further information.</p>

            <h3>8. User Obligations</h3>
            <p>You agree to:</p>
            <ul>
              <li>Ensure that all digital assets used for staking are from lawful and compliant sources</li>
              <li>Provide accurate account and verification information</li>
              <li>Not use staking services for money laundering, fraud, market manipulation, or other illegal activity</li>
              <li>Monitor your subscriptions, rewards, and redemption status through your AGCX account</li>
              <li>Comply with applicable laws, tax rules, and reporting obligations in your jurisdiction</li>
            </ul>
            <p>
              If AGCX determines that your assets or activity violate applicable law or platform rules, AGCX may freeze accounts, cancel subscriptions, withhold rewards, or deduct assets in accordance with the Terms of Use and relevant program rules.
            </p>

            <h3>9. AGCX Rights</h3>
            <p>AGCX and Lunexor s.r.o. reserve the right to:</p>
            <ul>
              <li>Launch, modify, suspend, or terminate any Staking Program at any time</li>
              <li>Adjust reward rates, caps, tiers, eligibility, or product parameters with notice where reasonably practicable</li>
              <li>Delist supported assets from staking due to regulatory, security, or operational reasons</li>
              <li>Require additional verification before allowing subscription, reward withdrawal, or redemption</li>
              <li>Offset amounts owed by you against staked assets or account balances where permitted by law</li>
            </ul>

            <h3>10. Service Fees and Taxes</h3>
            <ul>
              <li>Unless stated on the product page, AGCX Staking Programs are generally offered without a separate subscription fee</li>
              <li>Early withdrawal penalties, network fees, or third-party charges may apply as disclosed at subscription</li>
              <li>All taxes, duties, and reporting obligations related to staking rewards are your responsibility</li>
            </ul>

            <h3>11. Limitation of Liability</h3>
            <p>
              To the maximum extent permitted by applicable law, AGCX and Lunexor s.r.o. shall not be liable for indirect, incidental, special, or consequential losses arising from your use of staking services, including loss of profits, rewards, or digital asset value.
            </p>
            <p>
              Where liability cannot be excluded, it shall be limited to the total fees paid by you to AGCX for staking-related services during the twelve (12) months preceding the event giving rise to the claim, except where a higher limit is required by mandatory law.
            </p>

            <h3>12. Amendments</h3>
            <p>
              AGCX may update this Agreement from time to time. Updated versions will be published on the platform. Continued use of staking services after publication constitutes acceptance of the revised Agreement, except where applicable law requires explicit consent.
            </p>

            <h3>13. Governing Law and Disputes</h3>
            <p>
              This Agreement is governed by the laws of the Czech Republic. Disputes shall be subject to the exclusive jurisdiction of the courts of Prague, unless mandatory consumer protection rules in your jurisdiction provide otherwise.
            </p>

            <h3>14. Contact</h3>
            <p>
              For questions about this Agreement or AGCX Staking services, contact us through the{" "}
              <Link to="/contact">Contact</Link> page or via the support channels listed on the platform.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default StakingUserAgreement;
